<template>
  <div>
    <NavigationBar
      titel="Stundenmeldungen"
      :actions="actions"
      showBackBtn
      isPreview
      dense
    >
    </NavigationBar>

    <MasterDetailWrapper
      ref="masterDetail"
      :selectedId="selectedId"
      queryParamKey="timeSheetId"
      :syncRoute="true"
      :selectionDebounce="300"
      :initialSplitPosition="32"
      mobileAlertTitle="Split-Ansicht nicht verfügbar"
      mobileAlertText="Die Split-Ansicht ist auf kleinen Bildschirmen nicht verfügbar."
      :fallbackRoute="{
        name: 'accounting-time-sheets',
        params: { organizationId: $route.params.organizationId },
      }"
      fallbackRouteText="Zur Standardansicht"
      emptyStateIcon="mdi-clock-outline"
      emptyStateTitle="Keine Stundenmeldung ausgewählt"
      emptyStateSubtitle="Stundenmeldung auswählen, um Details anzuzeigen"
      @selection-change="onSelectionChange"
    >
      <template #master>
        <TimeSheetsList
          :timeSheets="timeSheets"
          :selectedId="selectedId"
          :loading="timeSheetsLoading"
          @select-time-sheet="onTimeSheetSelect"
        />
      </template>

      <template #detail>
        <TimeSheetDetails
          v-if="selectedTimeSheet"
          :key="selectedId"
          :timeSheetProp="selectedTimeSheet"
          :isEmbedded="true"
        />
      </template>
    </MasterDetailWrapper>

    <section v-if="checkIfSupport">
      <support-tools :sources="code"></support-tools>
    </section>
  </div>
</template>

<script>
import NavigationBar from "@/components/_systemwide/NavigationBar.vue";
import MasterDetailWrapper from "@/components/_systemwide/MasterDetailWrapper.vue";
import TimeSheetsList from "@/components/accounting/time-sheets/TimeSheetsList.vue";
import TimeSheetDetails from "@/views/accounting/time-sheets/TimeSheetDetails.vue";
import SupportTools from "@/components/_system/helpers/SupportTools.vue";
import { ACCOUNTING } from "@/store/modules.js";
import { GET_TIME_SHEETS } from "@/store/action-types.js";
import {
  ACCOUNTING_TIME_SHEETS_create,
  ACCOUNTING_TIME_SHEETS_read_all,
} from "@/data/permission-types.js";

export default {
  name: "accounting-time-sheets-split-view",
  components: {
    NavigationBar,
    MasterDetailWrapper,
    TimeSheetsList,
    TimeSheetDetails,
    SupportTools,
  },
  data() {
    return {
      selectedId: null,
      unsubscribeTimeSheets: null, // Store unsubscribe function for cleanup
      actions: [
        {
          title: "Erstellen",
          permission: `${ACCOUNTING_TIME_SHEETS_create}`,
          icon: "mdi-plus-circle",
          actionStyle: "textBtn",
          function: () => {
            this.$router.push({
              name: "accounting-time-sheets-new",
              params: {
                organizationId: this.$route.params.organizationId,
              },
            });
          },
        },
        { divider: true },
        { actionStyle: "clipboardBtn" },
      ],
    };
  },
  computed: {
    timeSheets() {
      return this.$store.state.accounting.timeSheets;
    },
    timeSheetsLoading() {
      return this.$store.state.accounting.loading;
    },
    selectedTimeSheet() {
      if (!this.selectedId) return null;
      return (
        this.timeSheets.find(
          (timeSheet) => timeSheet.meta?.id === this.selectedId,
        ) || null
      );
    },
    checkIfSupport() {
      return this.$store.getters["organization/checkIfSupport"];
    },
    code() {
      return [
        { title: "timeSheets", data: this.timeSheets },
        { title: "selectedTimeSheet", data: this.selectedTimeSheet },
      ];
    },
  },
  created() {
    console.log(
      "[TimeSheetsSplitView] Component created, initializing listeners",
    );
    this.initialize();
  },
  beforeDestroy() {
    console.log(
      "[TimeSheetsSplitView] Component being destroyed, cleaning up listeners",
    );
    this.cleanup();
  },
  methods: {
    async initialize() {
      // Initialize selection from route query
      if (this.$route.query.timeSheetId) {
        this.selectedId = this.$route.query.timeSheetId;
      }

      // Fetch time sheets with realtime listener
      const allowedToReadAllTimeSheets = this.$store.getters[
        "organization/checkPermissionByID"
      ](`${ACCOUNTING_TIME_SHEETS_read_all}`);

      const queryConfig = allowedToReadAllTimeSheets
        ? {
            organizationId: this.$route.params.organizationId,
            query: {
              queryIsFiltered: false,
            },
          }
        : {
            organizationId: this.$route.params.organizationId,
            query: {
              queryIsFiltered: true,
              onlyOwn: true,
            },
          };

      console.log(
        "[TimeSheetsSplitView] Setting up Firestore listener for time sheets",
        {
          allowedToReadAll: allowedToReadAllTimeSheets,
          queryConfig,
        },
      );

      // Store the unsubscribe function returned by the dispatch (must await because dispatch returns a Promise)
      this.unsubscribeTimeSheets = await this.$store.dispatch(
        `${ACCOUNTING}${GET_TIME_SHEETS}`,
        queryConfig,
      );

      console.log("[TimeSheetsSplitView] Firestore listener setup complete", {
        hasUnsubscribeFunction:
          typeof this.unsubscribeTimeSheets === "function",
      });
    },
    cleanup() {
      if (this.unsubscribeTimeSheets) {
        console.log(
          "[TimeSheetsSplitView] Unsubscribing from Firestore listener",
        );
        try {
          this.unsubscribeTimeSheets();
          console.log(
            "[TimeSheetsSplitView] Successfully unsubscribed from Firestore listener",
          );
        } catch (error) {
          console.error(
            "[TimeSheetsSplitView] Error unsubscribing from Firestore listener:",
            error,
          );
        }
        this.unsubscribeTimeSheets = null;
      } else {
        console.warn(
          "[TimeSheetsSplitView] No unsubscribe function available - listener may not have been set up correctly",
        );
      }
    },
    onTimeSheetSelect(timeSheet) {
      // Use the MasterDetailWrapper's selection handling with debounce
      this.$refs.masterDetail?.handleSelection(timeSheet.meta?.id);
    },
    onSelectionChange(id) {
      this.selectedId = id;
    },
  },
};
</script>
